/**
 * AttentionRow (KAN-628, KAN-734, KAN-774)
 *
 * One client row of the attention block: name, company, reason badges, the
 * reason text and the last client message. Clicking the row opens the dialog.
 * The date line and the action buttons on the right come from the view.
 * KAN-774: rows with nudge_exhausted / replied_silent and a phone get the
 * "Позвонить" tel: link in the right column (outside the row button, so no
 * nested interactive elements); `offered` is shown as chips under the text,
 * and the "Предлагали: ..." tail is cut from the text.
 * KAN-783: the "Признак" badge (lead_attribute) also gets the call link; the
 * configured lead attributes of the client are chips "name: value" under the text.
 * KAN-790: a manager_task row shows the reason title of the backend, the line
 * "Задача: <text>, до <due, organization time>" (task.text, a separate field) and the
 * "просрочено" mark; the call link and the chips as for nudge_exhausted.
 * KAN-810: the row is tinted by the lead temperature and shows its badge.
 * KAN-833: the call link is a click-to-call button (the platform calls the
 * manager, then connects the client); the phone stays visible as text.
 * KAN-815: the next bot touch ("Бот напишет ...") with its buttons is shown
 * under the row button (not inside it: no nested interactive elements).
 * KAN-857: the client Telegram avatar (initials without a picture) before the name.
 * KAN-864: the avatar sits left of the row button (it is a button itself: opens the picture).
 * KAN-918: `newMessage` shows the "Новое сообщение" badge (the view decides).
 * KAN-962: the organization stands in the title line right after the name. The name is
 * the row button; the rest of the text block opens the row on click, so the organization
 * link is not nested in a button.
 * KAN-965: the date stays on top of the right column; the actions and the call are one
 * row under it (attention-row-actions, flex-wrap), so the buttons do not make the row taller.
 */
import type { MouseEvent, ReactNode } from 'react'
import { CompanyNameLink } from '@/components/clientCompanies/CompanyNameLink'
import type { AttentionItem, AttentionReason } from '@/services/api/dashboard'
import {
  attentionItemTitle,
  attentionReasonText,
  attentionTask,
  formatTaskLine,
  hasOfferedReason,
  needsCall,
} from './attentionFormat'
import { ClientAvatar } from '@/components/clients/ClientAvatar'
import { ManagerCallButton } from '@/components/clients/ManagerCallButton'
import { LeadTemperatureBadge } from '@/components/leads/LeadTemperatureBadge'
import { leadTemperatureStyle } from '@/lib/leadTemperature'
import { NEW_MESSAGE_LABEL } from '@/lib/newMessage'
import { NextBotTouchNote } from '@/components/clients/NextBotTouchNote'

const REASON_LABELS: Record<AttentionReason, string> = {
  handed_to_manager: 'Передан менеджеру',
  needs_human: 'Нужен человек',
  dm_identified: 'Получен ЛПР',
  manual_mode: 'Ручной режим',
  unanswered: 'Ждёт ответа',
  dm_reached: 'Вышли на ЛПР',
  interested: 'Интерес',
  replied: 'Ответил',
  nudge_exhausted: 'Дожим исчерпан',
  replied_silent: 'Ответил и замолчал',
  product_declined: 'Отказался от товара',
  lead_attribute: 'Признак',
  manager_task: 'Задача менеджеру',
  idle: 'Нет движения',
  reopened: 'Возвращён в работу',
}

const REASON_STYLES: Record<AttentionReason, string> = {
  handed_to_manager: 'bg-violet-100 text-violet-700',
  needs_human: 'bg-rose-100 text-rose-700',
  dm_identified: 'bg-emerald-100 text-emerald-700',
  manual_mode: 'bg-sky-100 text-sky-700',
  unanswered: 'bg-amber-100 text-amber-700',
  dm_reached: 'bg-orange-100 text-orange-700',
  interested: 'bg-teal-100 text-teal-700',
  replied: 'bg-indigo-100 text-indigo-700',
  nudge_exhausted: 'bg-red-100 text-red-700',
  replied_silent: 'bg-slate-100 text-slate-700',
  product_declined: 'bg-gray-100 text-gray-600',
  lead_attribute: 'bg-yellow-100 text-yellow-800',
  manager_task: 'bg-fuchsia-100 text-fuchsia-700',
  idle: 'bg-orange-200 text-orange-800',
  reopened: 'bg-blue-50 text-blue-700',
}

interface AttentionRowProps {
  item: AttentionItem
  onOpen: (item: AttentionItem) => void
  /** Date line above the buttons (event time or "отработан ..."). */
  meta: string
  metaTestId?: string
  actions: ReactNode
  /** KAN-840: time zone of the organization for the task due (default Moscow). */
  timeZone?: string
  /** KAN-918: the client wrote after the manager took the client to work. */
  newMessage?: boolean
}

export function AttentionRow({
  item,
  onOpen,
  meta,
  metaTestId,
  actions,
  timeZone,
  newMessage = false,
}: AttentionRowProps) {
  const canOpen = !!item.estimate_id
  // KAN-774, KAN-790: chips only for the exhaust reasons and a manager task.
  const offered = hasOfferedReason(item) ? (item.offered ?? []) : []
  const reasonText = attentionReasonText(item)
  const task = attentionTask(item)
  const showCall = needsCall(item)
  // KAN-783: configured attributes with values; an older backend sends none.
  const attributes = item.attributes ?? []
  const temperature = leadTemperatureStyle(item.lead_temperature)
  const title = attentionItemTitle(item)
  const companyName = (item.client_company?.name || '').trim() || (item.company || '').trim()
  const showCompanyLink = !!item.client_company && !!companyName && companyName !== item.name
  // KAN-962: a click on the row text opens the dialog; clicks on links/buttons inside the
  // block (the name button, the organization link) and from portals (dialogs) are skipped.
  const handleMainClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!canOpen) return
    const target = event.target as HTMLElement
    if (!event.currentTarget.contains(target)) return
    if (target.closest('a, button')) return
    onOpen(item)
  }
  return (
    <li
      data-testid="attention-row"
      className={`flex flex-col gap-2 border-b px-2 py-3 last:border-b-0 sm:flex-row sm:items-start sm:gap-3 ${temperature?.row ?? ''}`}
    >
      <div className="flex w-full min-w-0 flex-1 flex-col gap-1">
        {/* KAN-864: the avatar opens the picture large, so it sits beside the row button, not in it. */}
        <div className="flex w-full min-w-0 items-start gap-2">
          <ClientAvatar avatarUrl={item.avatar_url} name={title} size={24} />
          {/* KAN-962: the clickable area is a plain block; the name is the row button (keyboard and
              screen readers), a click on the rest of the text opens the row too, a click on a link
              or a button inside (the organization) does its own thing. */}
          <div
            data-testid="attention-row-main"
            onClick={handleMainClick}
            className={`w-full min-w-0 rounded-md text-left ${canOpen ? 'cursor-pointer hover:bg-gray-50' : ''}`}
          >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <button
                type="button"
                data-testid="attention-row-open"
                onClick={() => onOpen(item)}
                disabled={!canOpen}
                className="max-w-full truncate rounded-sm text-left font-medium text-gray-900 disabled:cursor-default"
              >
                {title}
              </button>
              {/* KAN-962: the organization sits right after the name; a link, so not inside the button. */}
              {showCompanyLink && (
                <span
                  data-testid="attention-company"
                  title={companyName}
                  className="min-w-0 max-w-[16rem] truncate text-sm"
                >
                  <CompanyNameLink company={item.client_company} fallback={item.company} />
                </span>
              )}
              {item.company && item.company !== item.name && !item.client_company && (
                <span title={item.company} className="min-w-0 max-w-[16rem] truncate text-sm text-gray-500">
                  {item.company}
                </span>
              )}
              <LeadTemperatureBadge level={item.lead_temperature} />
              {newMessage && (
                <span
                  data-testid="attention-new-message"
                  className="whitespace-nowrap rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700"
                >
                  {NEW_MESSAGE_LABEL}
                </span>
              )}
              {item.reasons.map((reason) => (
                <span
                  key={reason}
                  className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${REASON_STYLES[reason]}`}
                >
                  {REASON_LABELS[reason]}
                </span>
              ))}
            </div>
            {reasonText && <p className="mt-1 text-sm text-gray-700">{reasonText}</p>}
            {task && (
              <p data-testid="attention-task" className="mt-1 text-sm text-gray-700">
                {formatTaskLine(task, timeZone)}
                {task.overdue && (
                  <span
                    data-testid="attention-task-overdue"
                    className="ml-2 inline-block whitespace-nowrap rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                  >
                    просрочено
                  </span>
                )}
              </p>
            )}
            {attributes.length > 0 && (
              <div data-testid="attention-attributes" className="mt-1 flex flex-wrap items-center gap-1 text-xs">
                {attributes.map((attribute, index) => (
                  <span
                    key={`${index}-${attribute.name}`}
                    className="max-w-full break-words rounded-2xl bg-yellow-50 px-2 py-0.5 text-yellow-900"
                  >
                    {attribute.name}: {attribute.value}
                  </span>
                ))}
              </div>
            )}
            {offered.length > 0 && (
              <div
                data-testid="attention-offered"
                className="mt-1 flex flex-wrap items-center gap-1 text-xs text-gray-500"
              >
                <span>Предлагали:</span>
                {offered.map((entry, index) => (
                  <span
                    key={`${index}-${entry}`}
                    className="max-w-full break-words rounded-2xl bg-gray-100 px-2 py-0.5 text-gray-700"
                  >
                    {entry}
                  </span>
                ))}
              </div>
            )}
            {item.last_message && (
              <p className="mt-0.5 truncate text-sm text-gray-500">Клиент: {item.last_message.content}</p>
            )}
          </div>
        </div>
        <NextBotTouchNote clientId={item.client_id} touch={item.next_bot_touch} />
      </div>
      <div
        data-testid="attention-row-side"
        className="flex w-full flex-col items-start gap-1 sm:w-auto sm:max-w-[60%] sm:shrink-0 sm:items-end"
      >
        <span data-testid={metaTestId} className="text-xs text-gray-400">
          {meta}
        </span>
        {/* KAN-965: all controls in one row under the date; wraps on a narrow screen. */}
        <div
          data-testid="attention-row-actions"
          className="flex max-w-full flex-wrap items-center justify-start gap-1.5 sm:justify-end"
        >
          {actions}
          {showCall && item.phone && (
            <ManagerCallButton
              clientId={item.client_id}
              phone={item.phone}
              testId="attention-call-link"
              targetMode="onClick"
            />
          )}
        </div>
      </div>
    </li>
  )
}
